import {
  Entity,
  ExploreInsightDirection,
  formatTicker,
  InsightDirection,
  WatchlistRelation,
} from '@toggle/toggle';
import { t } from 'i18next';

import { Watchlist } from '~/stores/use-watchlist/watchlist-types';
import { getConditionIcons } from '~/views/insights/components/insight-icons/utils';

import { InsightCardType } from '../../types';

const getWatchlistTooltip = ({
  ticker,
  foundWatchlists,
}: {
  ticker: string;
  foundWatchlists: Watchlist[];
}) => {
  const relation = t('whatsNew:first');
  const name = foundWatchlists[0].name;
  let relationTooltip = t('whatsNew:watchlistRelationPrefix', {
    entity: ticker,
    watchlist: name,
  });

  if (foundWatchlists.length > 1) {
    relationTooltip += ` ${t('whatsNew:andOthers', {
      count: foundWatchlists.length - 1,
    })}`;
  }

  return {
    relation,
    relationTooltip,
  };
};

const getCompetitorTooltip = ({
  competitorEntity,
  primaryTicker,
  relationCompetitors,
  activeWatchlists,
}: {
  competitorEntity: Entity;
  primaryTicker: string;
  relationCompetitors: string[];
  activeWatchlists: Watchlist[];
}) => {
  const relation = t('whatsNew:second');
  let relationTooltip = t('whatsNew:competitorPrefix', {
    primary: primaryTicker,
    competitor: formatTicker(competitorEntity),
  });

  if (relationCompetitors.length > 1) {
    relationTooltip += ` ${t('whatsNew:andOthers', {
      count: relationCompetitors.length - 1,
    })}`;
  }

  const competitorTagSet = new Set(relationCompetitors);
  const foundWatchlists = activeWatchlists.filter(wl =>
    wl.entities.some(e => competitorTagSet.has(e.tag))
  );
  const competitorWatchlist = foundWatchlists.find(wl =>
    wl.entities.some(e => e.tag === competitorEntity.tag)
  );

  if (foundWatchlists.length > 0 && competitorWatchlist) {
    relationTooltip += t('whatsNew:competitorSuffix', {
      watchlist: competitorWatchlist.name,
      count: relationCompetitors.length,
    });

    if (foundWatchlists.length > 1) {
      relationTooltip += ` ${t('whatsNew:andOthers', {
        count: foundWatchlists.length - 1,
      })}`;
    }
  }

  return {
    relation,
    relationTooltip,
  };
};

type GetRelationTextProps = {
  relationData?: WatchlistRelation;
  allWatchlists: Watchlist[];
  primaryEntityTag: string;
  entitiesData: Entity[];
  activeWatchlists: Watchlist[];
};

export const getRelationText = ({
  relationData,
  allWatchlists,
  primaryEntityTag,
  entitiesData,
  activeWatchlists,
}: GetRelationTextProps) => {
  const relationCompetitors = relationData?.competitor_of ?? [];
  const relationWatchlists = relationData?.watchlist ?? [];
  const primaryEntity = entitiesData.find(e => e.tag === primaryEntityTag);

  if (relationData && primaryEntity) {
    const formattedPrimaryTicker = formatTicker(primaryEntity);
    const competitorsLength = relationCompetitors.length;
    const foundWatchlists = relationWatchlists
      .map(w => allWatchlists.find(wl => wl.id === w))
      .filter(x => !!x);
    const isWatchlist = foundWatchlists.length > 0;

    if (isWatchlist) {
      const { relation, relationTooltip } = getWatchlistTooltip({
        ticker: formattedPrimaryTicker,
        foundWatchlists,
      });
      return {
        relation,
        relationTooltip,
      };
    } else if (competitorsLength > 0) {
      const competitorEntity = entitiesData.find(
        e => e.tag === relationCompetitors[0]
      );

      if (competitorEntity) {
        const { relation, relationTooltip } = getCompetitorTooltip({
          competitorEntity,
          relationCompetitors,
          activeWatchlists,
          primaryTicker: formattedPrimaryTicker,
        });
        return { relation, relationTooltip };
      }
    }
  }

  return {};
};

export const getInsightData = (insight: InsightCardType) => {
  const conditions = getConditionIcons({
    conditions: insight.conditions,
    kind: insight.metadata.kind,
  });
  const isCombined = conditions.length > 1;
  const median = insight.metadata?.median_return;
  const horizon = insight.metadata?.horizon;
  const stars = insight.metadata?.stars;

  return {
    isCombined,
    conditions,
    median,
    horizon,
    stars,
  };
};

export const getExploreDirection = (
  direction: InsightDirection
): ExploreInsightDirection => {
  switch (direction) {
    case InsightDirection.Unspecified:
      return ExploreInsightDirection.Bullish;
    case InsightDirection.Bullish:
      return ExploreInsightDirection.Bullish;
    default:
      return ExploreInsightDirection.Bearish;
  }
};
